import { axiosInstance } from "@/api/api-client";
import { createNewFolder } from "@/hooks/media-api";
import { useMediaLibrary } from "@/hooks/use-media-library";
import { useToast } from "@/hooks/use-toast";
import { MediaAsset } from "@/types/media";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { MediaSidebarPanel } from "./MediaSidebarPanel";
import { MediaSearchBar } from "./MediaSearchBar";
import { MediaGridPane } from "./MediaGridPane";
import { MediaUploader } from "./MediaUploader";
import { Button } from "../ui/button";
import { Check, Copy, UploadCloud, Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type MediaBrowserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  multi?: boolean;
  initialParentId?: string | null;
  allowUpload?: boolean;
  onPick: (items: MediaAsset[]) => void;
  title?: string;
  description?: string;
};

export function MediaBrowserDialog({
  open,
  onOpenChange,
  multi = true,
  initialParentId = null,
  allowUpload = true,
  onPick,
  title = "Browse Media",
  description = "Find, preview, and select files.",
}: MediaBrowserDialogProps) {
  const { toast } = useToast();
  const state = useMediaLibrary(initialParentId);
  const [activeView, setActiveView] = useState<"browse" | "upload">("browse");

  useEffect(() => {
    if (!open) return;
    state.fetchFolderTree(state.currentFolder);
    state.fetchMediaAssets({
      reset: true,
      parentId: state.currentFolder?.id || null,
    });
    state.setSelectedIds([]);
    setActiveView("browse");
  }, [open]);

  // Auto-pick when single selection mode and an item is selected
  useEffect(() => {
    if (!multi && state.selectedItems.length === 1) {
      onPick(state.selectedItems);
      onOpenChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, state.selectedItems]);

  const handleUse = () => {
    if (state.selectedItems.length === 0) {
      return toast({ title: "Nothing selected", variant: "destructive" });
    }
    onPick(state.selectedItems);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1100px] w-full md:w-[95vw] h-full md:h-[85vh] p-0 bg-white flex flex-col gap-0 overflow-hidden border-none md:border md:rounded-xl">

        {/* Header - Fixed at top */}
        <DialogHeader className="px-4 py-3 md:px-6 md:pt-6 md:pb-2 border-b md:border-none shrink-0">
          <DialogTitle className="text-lg md:text-xl">{title}</DialogTitle>
          <DialogDescription className="hidden md:block text-xs">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Main Body Wrapper 
            Critical change: flex-col (mobile) to flex-row (desktop)
        */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">

          {/* FOLDER NAV: 
              On mobile: Sits at the top as a full-width button trigger.
              On desktop: Sits on the left as a 80px wide sidebar.
          */}
          <MediaSidebarPanel
            state={state}
            onCreateFolder={async (name) => {
              const parentId = state.currentFolder ? state.currentFolder.id : null;
              const path = `${state.currentFolder?.fileUrl || ""}/${name}`;
              const res = await createNewFolder(parentId, name, path);
              if (res.success) {
                await state.fetchFolderTree(state.currentFolder);
                await state.fetchMediaAssets({ reset: true, parentId: res.data.id });
              }
            }}
          />

          {/* RIGHT COLUMN: Search + Content + Footer */}
          <div className="flex-1 flex flex-col min-w-0 bg-slate-50/30 overflow-hidden">

            {/* View Switcher Tabs */}
            <div className="px-4 py-2 bg-white border-b shrink-0">
              <Tabs value={activeView} onValueChange={(v) => setActiveView(v as any)}>
                <TabsList className="h-8">
                  <TabsTrigger value="browse" className="text-xs gap-1.5">
                    <Search className="h-3.5 w-3.5" /> Browse
                  </TabsTrigger>
                  {allowUpload && (
                    <TabsTrigger value="upload" className="text-xs gap-1.5">
                      <UploadCloud className="h-3.5 w-3.5" /> Upload
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {activeView === "browse" ? (
                <>
                  <div className="bg-white px-2 py-1 md:px-4 shrink-0">
                    <MediaSearchBar state={state} />
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <MediaGridPane
                      state={state}
                      multi={multi}
                      onCopyUrl={async (item) => {
                        const url = (item as any).fileUrl || (item as any).s3Url || "";
                        await navigator.clipboard.writeText(url);
                        toast({ title: "URL Copied" });
                      }}

                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 overflow-auto bg-white">
                  <MediaUploader state={state} />
                </div>
              )}
            </div>

            {/* Sticky Action Footer */}
            <div className="border-t bg-white p-3 md:p-4 shrink-0">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center justify-between w-full sm:w-auto sm:gap-4">
                  <span className="text-xs font-semibold text-slate-700">
                    {state.selectedItems.length} selected
                  </span>
                  {state.selectedItems.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={() => {
                        const urls = state.selectedItems.map(i => (i as any).fileUrl || "").join("\n");
                        navigator.clipboard.writeText(urls);
                        toast({ title: "URLs Copied" });
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copy All
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none h-9 text-xs"
                    onClick={() => onOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUse}
                    disabled={state.selectedItems.length === 0}
                    className="flex-1 sm:flex-none h-9 text-xs gap-2 shadow-sm"
                  >
                    <Check className="h-4 w-4" /> {multi ? "Pick Files" : "Pick File"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}